/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, ValidatorFn, Validators} from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {ToggleButtonOptionModel} from '../../../shared/toggle-button/toggle-button-option.model';
import {BusinessCategoryBaseModel} from '../../../api/ms-claims/contracts/business-category-base.model';
import {ClaimErrorBaseModel} from '../../../api/ms-claims/contracts/claim-error-base.model';
import {DecisionBaseModel} from '../../../api/ms-claims/contracts/decision-base.model';
import {Observable, of, Subscription, timer} from 'rxjs';
import {TypeaheadHelperService} from '../../../shared/services/typeahead-helper.service';
import {ClaimPositionDataModel} from '../../models/claim-position-data.model';
import {WarningsEnum} from '../../../shared/warnings/warnings.enum';
import {InputTextComponent} from '../../../shared/input/input-text/input-text.component';
import {HandlingUnitApi} from '../../../api/ms-claims/handling-unit.api';
import {
    HandlingUnitValidationResultModel
} from '../../../api/ms-claims/contracts/handling-unit-validation-result.model';
import {MessageBaseModel} from '../../../api/generic/contracts/message-base.model';
import {SeverityEnum} from '../../../api/generic/contracts/severity.enum';
import {InputMessagesService} from '../../../shared/input/service/input-messages.service';
import {MandatoryMessageTypeEnum} from '../../../shared/input/service/mandatory-message-type.enum';
import {InputDropdownComponent} from '../../../shared/input/input-dropdown/input-dropdown.component';
import {InputUnitsComponent} from '../../../shared/input/input-units/input-units.component';
import {objectsValidator} from '../../../shared/input/input-validators';
import {DecisionDetailModel} from '../../../api/ms-claims/contracts/decision-detail.model';
import {MaterialPositionMessageErrorModel} from './models/material-position-message-error.model';
import {MaterialPositionErrorCodeEnum} from './models/material-position-error-code.enum';

@Component({
    selector: 'app-material-position-modal',
    templateUrl: './material-position-modal.component.html',
    styleUrls: ['./material-position-modal.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MaterialPositionModalComponent implements OnInit, OnDestroy {

    /** Input variables for the material dependent information **/
    @Input() partnerCode: string;
    @Input() supplyingPackingPlantCode: string;
    @Input() claimCodeCode: string;
    @Input() material: { number: string, description: string, salesUnit: string };
    @Input() businessCategoryOptions: BusinessCategoryBaseModel[] = [];
    @Input() numberOfPositions: number;
    @Input() maxNumberOfPositions: number;
    @Input() excludedDecisionCodes: DecisionBaseModel[];
    /** Input variables for the dropdown lists **/
    @Input() errorCodeOptions: ClaimErrorBaseModel[] = [];
    @Input() decisionCodeOptions: DecisionDetailModel[] = [];
    @Input() decisionCodeExcludedOptions: DecisionDetailModel[] = [];
    /** Variable for the edition selected position **/
    @Input() selectedPosition: ClaimPositionDataModel;
    /** Input variables */
    @ViewChild('quantityInput', {static: true}) quantityInput: InputUnitsComponent;
    @ViewChild('decisionCodeInput', {static: true}) decisionCodeInput: InputDropdownComponent;
    @ViewChild('errorCodeInput', {static: true}) errorCodeInput: InputDropdownComponent;
    @ViewChild('handlingUnitInput', {static: true}) handlingUnitInput: InputTextComponent;
    @ViewChild('timeEffortInput', {static: true}) timeEffortInput: InputTextComponent;
    /** Form value output variables **/
    @Output() addClick = new EventEmitter<ClaimPositionDataModel>();
    /** Variables for the toggle button */
    public toggleOptions: ToggleButtonOptionModel[] = [];
    public toggleButtonSelectedOption: ToggleButtonOptionModel;
    public resetBusinessCategory = new EventEmitter<boolean>();
    /** Variable for the handling unit validation result */
    public handlingUnit: HandlingUnitValidationResultModel;
    public decisionRequiresTimeEffort: boolean;
    /** Input messages variables */
    public quantityMessages = new Map<WarningsEnum, string[]>();
    public decisionCodeMessages = new Map<WarningsEnum, string[]>();
    public errorCodeMessages = new Map<WarningsEnum, string[]>();
    public handlingUnitMessages = new Map<WarningsEnum, string[]>();
    public timeEffortMinutesMessages = new Map<WarningsEnum, string[]>();
    public timeEffortRequired: WarningsEnum;
    /** Input validators */
    validators: { [key: string]: ValidatorFn[] } = {
        quantity: [Validators.pattern(/^[0-9]{0,6}$/)],
        decision: [objectsValidator],
        claimError: [objectsValidator],
        handlingUnit: [Validators.pattern(/^[0-9]{14}$/)],
        timeEffort: [Validators.pattern(/^[1-9][0-9]{0,4}$/)]
    };

    public warningBoxLevel: WarningsEnum;
    public showWarningBoxHU: boolean;
    public showWarningBoxDecisionCode: boolean;
    public modalWarningClass: string;
    public isLoading = false;
    WarningsEnum = WarningsEnum;
    public errorMessage: string;

    private modalValues: ClaimPositionDataModel = {
        quantity: null,
        decision: null,
        claimError: null,
        handlingUnit: null,
        handlingUnitValidation: null,
        timeEffort: null,
        businessCategory: null
    };
    private readonly HU_MAX_LENGTH = 14;
    private handlingUnitValidationResult: HandlingUnitValidationResultModel;
    private subscriptions = new Subscription();

    constructor(private formBuilder: UntypedFormBuilder,
                public activeModal: NgbActiveModal,
                public typeahead: TypeaheadHelperService,
                private handlingUnitApi: HandlingUnitApi,
                private inputMessagesService: InputMessagesService) {
    }

    get getMessageErrorInfo(): MaterialPositionMessageErrorModel {
        return {
            partnerCode: this.partnerCode,
            handlingUnit: this.getHandlingUnit(),
            materialNumber: this.material.number,
            supplyingPackingPlantCode: this.supplyingPackingPlantCode
        } as MaterialPositionMessageErrorModel;
    }

    getHandlingUnit(): string {
        return this.handlingUnit != null ? this.handlingUnit?.handlingUnitNumber : this.modalValues?.handlingUnit;
    }

    ngOnInit(): void {
        this.filterExcludedDecisions();
        this.mapBusinessToggleOptions();

        timer(0)
                .subscribe(() => {
                    this.setFormInitialValues();
                });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    /** Sets the quantity form value */
    setQuantityValue = (quantity: string): void => {
        this.modalValues.quantity = quantity != null ? Number(quantity) : null;
    };

    /** Handles quantity errors according to the input status */
    manageQuantityStatusChange = (quantity: AbstractControl): void => {
        this.inputMessagesService.invalidInputValueError(quantity,
                this.quantityMessages,
                this.quantityInput.label);
        this.inputMessagesService.mandatoryWarnings(quantity, this.quantityMessages, MandatoryMessageTypeEnum.SEND);
        this.quantityMessages = new Map<WarningsEnum, string[]>(this.quantityMessages);
    };

    /** Sets the decision code form value */
    setDecisionCodeValue = (decision: DecisionBaseModel): void => {
        if (this.decisionCodeInput.isValid) {
            this.modalWarningClass = null;
            this.showWarningBoxDecisionCode = false;
            this.modalValues.decisionIsValid = true;
        }
        this.modalValues.decision = decision;
        this.enableTimeEffort(decision);
    };

    /** Handles decision code errors according to the input status */
    manageDecisionCodeStatusChange = (decision: AbstractControl): void => {
        this.inputMessagesService.invalidInputValueError(decision,
                this.decisionCodeMessages,
                this.decisionCodeInput.label);
        this.inputMessagesService.mandatoryWarnings(decision, this.decisionCodeMessages, MandatoryMessageTypeEnum.SEND);
        this.decisionCodeMessages = new Map<WarningsEnum, string[]>(this.decisionCodeMessages);
    };

    /** Sets the error code form value */
    setErrorCodeValue = (error: ClaimErrorBaseModel): void => {
        this.modalValues.claimError = error;
    };

    /** Handles error code errors according to the input status */
    manageErrorCodeStatusChange = (error: AbstractControl): void => {
        this.inputMessagesService.invalidInputValueError(error,
                this.errorCodeMessages,
                this.errorCodeInput.label);
        this.inputMessagesService.mandatoryWarnings(error, this.errorCodeMessages, MandatoryMessageTypeEnum.SEND);
        this.errorCodeMessages = new Map<WarningsEnum, string[]>(this.errorCodeMessages);
    };

    /** Sets the time effort form value */
    setTimeEffortValue = (timeEffort: string): void => {
        this.modalValues.timeEffort = timeEffort != null && timeEffort !== '' ? Number(timeEffort) : null;
    };

    /** Handles time effort errors according to the input status */
    manageTimeEffortStatusChange = (timeEffort: AbstractControl): void => {
        this.inputMessagesService.invalidInputValueError(timeEffort,
                this.timeEffortMinutesMessages,
                `${this.timeEffortInput.label} value`);
        this.inputMessagesService.mandatoryWarnings(timeEffort, this.timeEffortMinutesMessages, MandatoryMessageTypeEnum.SEND);
        this.timeEffortMinutesMessages = new Map<WarningsEnum, string[]>(this.timeEffortMinutesMessages);
    };

    /** Manages time effort input enable/disable */
    enableTimeEffort = (decision: DecisionBaseModel): void => {
        if (decision && decision.hasTimeEffort) {
            this.timeEffortInput.enable();
            this.decisionRequiresTimeEffort = decision.hasTimeEffort;
        } else {
            this.timeEffortInput.reset();
            this.timeEffortInput.disable();
            this.decisionRequiresTimeEffort = false;
        }
        this.timeEffortRequired = this.decisionRequiresTimeEffort ? WarningsEnum.INFO : null;
    };

    /** Sets the handling unit form value */
    setHandlingUnitValue = (value: string): void => {
        this.modalValues.handlingUnit = value === '' ? null : value;
        if (value == null || value.length >= 0 && value.length < this.HU_MAX_LENGTH) {
            this.modalWarningClass = null;
            this.showWarningBoxHU = false;
        }
    };

    /** Handles handling unit errors according to the input status */
    manageHandlingUnitStatusChange = (handlingUnit: AbstractControl): void => {
        this.inputMessagesService.invalidInputValueError(handlingUnit,
                this.handlingUnitMessages,
                this.handlingUnitInput.label,
                '00000000000000');
        this.inputMessagesService.mandatoryWarnings(handlingUnit, this.handlingUnitMessages, MandatoryMessageTypeEnum.SEND);
        this.handlingUnitMessages = new Map<WarningsEnum, string[]>(this.handlingUnitMessages);
    };

    /** Validates the handling unit form value */
    validateHandlingUnit = (): void => {
        const handlingUnit = this.modalValues.handlingUnit;
        if (this.handlingUnitInput.isValid && handlingUnit) {
            this.isLoading = true;
            this.handlingUnitApi.validateHandlingUnit(this.supplyingPackingPlantCode, this.material.number,
                    this.partnerCode, handlingUnit)
                    .subscribe((result) => {
                        this.updateHandlingUnit(result);
                    }, (error) => {
                        const handlingUnitValidationResultModel = {
                            handlingUnitNumber: handlingUnit,
                            valid: null,
                            message: {
                                description: error.error.message,
                                severity: SeverityEnum.WARNING
                            } as MessageBaseModel
                        } as HandlingUnitValidationResultModel;
                        this.updateHandlingUnit(handlingUnitValidationResultModel);
                    })
                    .add(() => {
                        this.isLoading = false;
                    });
        }
    };

    /**
     * Update the handling unit according the handlingUnitValidationResultModel
     * @param handlingUnitValidationResultModel the hu validation result
     */
    updateHandlingUnit(handlingUnitValidationResultModel: HandlingUnitValidationResultModel): void {
        this.handlingUnitValidationResult = handlingUnitValidationResultModel;
        this.modalValues.handlingUnitValidation = handlingUnitValidationResultModel.message;
        this.updateHandlingUnitInput(handlingUnitValidationResultModel);
        this.manageWarningBoxLevelAndVisibility(handlingUnitValidationResultModel.message, this.getMessageErrorInfo);
    }

    /** Updates the handling unit input status and warnings
     * @param result the handling unit validation result report
     */
    updateHandlingUnitInput = (result: HandlingUnitValidationResultModel): void => {
        if (result.valid === null || result.valid) {
            this.handlingUnitInput.isValid = true;
        } else {
            this.handlingUnitInput.isValid = false;
        }
    };

    /**
     Manages Handling unit warning box level and visibility
     Sets the modal class
     Sets the handling unit input error status
     @param handlingUnitValidation the handling unit validation result report
     */
    manageWarningBoxLevelAndVisibility = (handlingUnitValidation: MessageBaseModel,
                                          messageErrorInfo: MaterialPositionMessageErrorModel): void => {
        this.showWarningBoxHU = false;
        this.modalWarningClass = null;
        this.handlingUnitInput.isValid = true;

        if (handlingUnitValidation) {
            switch (handlingUnitValidation.severity) {
                case SeverityEnum.ERROR:
                    this.showWarningBoxHU = true;
                    this.warningBoxLevel = WarningsEnum.ERROR;
                    this.modalWarningClass = 'modal-header-error';
                    this.handlingUnitInput.isValid = false;
                    this.errorMessage = this.getErrorMessage(handlingUnitValidation.code, messageErrorInfo);
                    break;
                case SeverityEnum.WARNING:
                    this.showWarningBoxHU = true;
                    this.warningBoxLevel = WarningsEnum.WARNING;
                    this.modalWarningClass = 'modal-header-warning';
                    this.handlingUnitInput.isValid = true;
                    break;
            }
        }
    };

    /** Maps business toggle options */
    mapBusinessToggleOptions = (): void => {
        this.toggleOptions = this.businessCategoryOptions.map((option) => ({
            code: option.code,
            description: option.name,
            extra: option.range,
            active: option.active
        }));
    };

    /** Manages decision codes exclusions */
    filterExcludedDecisions = (): void => {
        this.decisionCodeExcludedOptions = this.decisionCodeOptions
                .filter((d) => !this.excludedDecisionCodes.some((e) => e ? e.code === d.code : false));
    };

    /** Sets the businessCategory form control value */
    setBusinessCategoryValue = (value: ToggleButtonOptionModel): void => {
        if (value) {
            this.modalValues.businessCategory = {
                code: value.code,
                name: value.description,
                range: value.extra,
                active: value.active
            };
        }
    };

    isValidForAddAndNew = (): boolean => this.isValidForAdd() && this.numberOfPositions < this.maxNumberOfPositions - 1;

    isValidForAdd = (): boolean => this.isValid() && this.numberOfPositions < this.maxNumberOfPositions;

    isValidForEdit = (): boolean => this.isValid();

    /** Checks if form value is valid to save */
    isValid = (): boolean => this.quantityInput.isValid
            && this.decisionCodeInput.isValid
            && this.errorCodeInput.isValid
            && this.handlingUnitInput.isValid
            && this.timeEffortInput.isValid;

    /** Sets form initial value if in edit mode */
    setFormInitialValues(): void {
        if (this.selectedPosition) {
            this.quantityInput.setValue(this.selectedPosition.quantity);
            this.decisionCodeInput.setValue(this.selectedPosition.decision);
            this.errorCodeInput.setValue(this.selectedPosition.claimError);
            this.handlingUnitInput.setValue(this.selectedPosition.handlingUnit);
            this.timeEffortInput.setValue(this.selectedPosition.timeEffort);
            this.modalValues.businessCategory = this.selectedPosition.businessCategory;
            this.modalValues.handlingUnitValidation = this.selectedPosition.handlingUnitValidation;

            if (this.selectedPosition.businessCategory != null) {
                this.toggleButtonSelectedOption = {
                    code: this.selectedPosition.businessCategory.code,
                    description: this.selectedPosition.businessCategory.name,
                    extra: this.selectedPosition.businessCategory.range
                };
            }
            this.manageWarningBoxLevelAndVisibility(this.selectedPosition.handlingUnitValidation, this.getMessageErrorInfo);
            if (!this.selectedPosition.decisionIsValid) {
                this.modalWarningClass = 'modal-header-error';
                this.showWarningBoxDecisionCode = true;
                this.warningBoxLevel = WarningsEnum.ERROR;
                this.decisionCodeInput.isValid = false;
            }
        } else {
            this.timeEffortInput.disable();
        }
    }

    /**
     * Error code input filter function
     */
    searchErrorCodes = (term: string): Observable<any> => {
        if (term != null && term.length > 0) {
            return of(this.errorCodeOptions.filter((errorCode) => (`${errorCode.code} - ${errorCode.name}`)
                    .toLowerCase()
                    .indexOf(term.toLowerCase()) > -1));
        }
        return of(this.errorCodeOptions);
    };

    /**
     * Decision code input filter function
     */
    searchDecisionCodes = (term: string): Observable<any> => {
        if (term != null && term.length > 0) {
            return of(this.decisionCodeExcludedOptions.filter((decisionCode) => (`${decisionCode.code} - ${decisionCode.name}`)
                    .toLowerCase()
                    .indexOf(term.toLowerCase()) > -1));
        }
        return of(this.decisionCodeExcludedOptions);
    };

    emitPosition = (): void => {
        this.modalValues.handlingUnitValidation = this.prepareMessageToEmitPosition();
        this.addClick.emit({...this.modalValues});
    };

    prepareMessageToEmitPosition(): MessageBaseModel {
        let handlingUnitValidation: MessageBaseModel = null;
        if (this.selectedPosition && this.handlingUnitValidationResult == null) {
            handlingUnitValidation = this.selectedPosition.handlingUnitValidation;
        } else if (this.handlingUnitValidationResult != null && this.handlingUnitInput.isValid && this.modalValues.handlingUnit != null) {
            handlingUnitValidation = this.handlingUnitValidationResult.message;
        }
        return handlingUnitValidation;
    }

    add = (): void => {
        this.emitPosition();
        this.activeModal.close();
    };

    addAndNew = (): void => {
        this.emitPosition();
        this.filterExcludedDecisions();
        this.resetBusinessCategory.emit(true);
        this.resetForm();
    };

    getErrorMessage(code: string, messageErrorInfo: MaterialPositionMessageErrorModel): string {
        switch (code) {
            case MaterialPositionErrorCodeEnum['INVALID_HU_NOT_FOUND_SUPPLYING_PACKING_PLANT']:
                return `HU <strong>${messageErrorInfo.handlingUnit}</strong> with material ${messageErrorInfo.materialNumber}
            <strong>was not found</strong> in the selected supplying packing plant ${messageErrorInfo.supplyingPackingPlantCode}. 
            <strong>Please check your entries.</strong>`;
                break;
            case MaterialPositionErrorCodeEnum['INVALID_HU_NOT_FOUND_PARTNER']:
                return `HU ${messageErrorInfo.handlingUnit} <strong>is not associated</strong> to the partner 
            ${messageErrorInfo.partnerCode}. Please check your entries.`;
                break;
            default:
                break;
        }
    }

    private resetForm = (): void => {
        this.quantityInput.reset();
        this.decisionCodeInput.reset();
        this.errorCodeInput.reset();
        this.handlingUnitInput.reset();
        this.timeEffortInput.reset();
    };
}
